import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import {Link as RouterLink} from 'react-router';
import {subscribeToExpert} from '../../firebase/firestore/experts';
import {getFintelsByAuthorUid} from '../../firebase/firestore/reports';
import {formatDate} from '../../utils/formatTimestamp';
import {Box, Button, Chip, CircularProgress, Divider, Link, Paper, Stack, Tooltip, Typography} from '@mui/material';
import SendMessageButton from '../../components/SendMessageButton';
import UserAvatar from '../../components/UserAvatar';
import {useSession} from "../../firebase/UserProvider";
import {resolveExpertAvatarUrl} from '../../utils/expertAvatar';

const relevanceTooltip = 'Computed by AI. Takes into account AoR, comments, and papers authored. Not shared externally.';

const DetailRow = ({label, children}) => (
    <Stack spacing={0.5}>
        <Typography variant='overline' sx={{color: 'text.secondary', lineHeight: 1.2}}>{label}</Typography>
        <Typography variant='body1'>{children}</Typography>
    </Stack>
);

const Section = ({title, children}) => (
    <Stack spacing={1.5} flexGrow={1}>
        <Typography variant='subtitle1' sx={{fontWeight: 700}}>{title}</Typography>
        {children}
    </Stack>
);

const ListAsChips = ({items}) => {
    const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];
    if (!safeItems.length) return <Typography color='text.secondary'>—</Typography>;
    return (
        <Stack direction='row' gap={1} flexWrap='wrap'>
            {safeItems.map((item, idx) => (
                <Chip key={`${item}-${idx}`} label={item} variant='outlined'/>
            ))}
        </Stack>
    );
};

const asDisplayValue = (value) => {
    if (value === null || value === undefined) return '—';
    const s = String(value).trim();
    return s.length ? s : '—';
};

function formatRelativeTime(ts) {
    if (!ts) return null;
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(date.getTime())) return null;
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
}

function formatShortDate(ts) {
    return formatDate(ts, '');
}

export default function ExpertPage() {
    const {user} = useSession();
    const {handle} = useParams();
    const navigate = useNavigate();

    // DB subscription — null = not found, undefined = still loading
    const [expert, setExpert] = useState(undefined);

    useEffect(() => {
        setExpert(undefined);
        return subscribeToExpert(handle, (data) => setExpert(data ?? null));
    }, [handle]);

    // Recent contributions: up to 5 latest approved FINTELs authored by this expert
    const [recentFintels, setRecentFintels] = useState(null); // null = loading

    useEffect(() => {
        setRecentFintels(null);
        const expertUid = expert?.uid;
        if (!expertUid) return;
        let cancelled = false;
        getFintelsByAuthorUid(expertUid)
            .then((all) => {
                if (!cancelled) setRecentFintels((all || []).slice(0, 5));
            })
            .catch((err) => {
                console.error('[ExpertPage] Failed to load recent FINTELs', err);
                if (!cancelled) setRecentFintels([]);
            });
        return () => { cancelled = true; };
    }, [expert?.uid]);

    const headshotUrl = resolveExpertAvatarUrl(expert) || null;

    const chatUser = useMemo(() => {
        if (!expert?.uid) return null;
        const displayName = expert.displayName || expert.name || expert.handle;
        return {
            userDisplayName: displayName,
            displayName,
            headshot: headshotUrl,
            agency: expert.organization,
            office: expert.office,
            poc: expert.poc,
            contact: expert.contact,
            title: expert.title,
            email: expert.email,
            uid: expert.uid
        };
    }, [expert, headshotUrl]);

    if (expert === undefined) {
        // Still loading
        return (
            <Stack p={4} alignItems='center'>
                <CircularProgress size={32}/>
            </Stack>
        );
    }

    if (!expert) {
        return (
            <Stack p={3} spacing={2} alignItems='flex-start'>
                <Typography variant='h5'>Expert not found</Typography>
                <Button variant='outlined' onClick={() => navigate('/experts')}>Back to Experts</Button>
            </Stack>
        );
    }

    const formatPhone = (value) => {
        if (!value) return '—';
        const digits = String(value).replace(/\D/g, '');
        if (digits.length === 10) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        return String(value).trim();
    };

    const formatTsVoip = (value) => {
        if (!value) return '—';
        const digits = String(value).replace(/\D/g, '');
        if (digits.length === 7) {
            return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        }
        return String(value).trim();
    };

    const paletteKey = expert.relevance === 'high' ? 'success' : expert.relevance === 'medium' ? 'info' : 'warning';
    const updatedAgo = formatRelativeTime(expert.expertiseUpdatedAt);

    const twoCol = {
        direction: {xs: 'column', sm: 'row'},
        spacing: {xs: 1.5, sm: 3},
        alignItems: {xs: 'stretch', sm: 'flex-start'}
    };

    return (
        <Stack width={'100%'} overflow={'auto'} pb={4}>

            <Stack p={2} maxWidth={980} width={980} spacing={2}>
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Button variant='outlined' onClick={() => navigate('/experts')}>Back to Experts</Button>
                </Stack>

                <Paper elevation={0}
                       sx={{p: 3, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}`}}>
                    <Stack spacing={3}>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={3}
                               alignItems={'flex-start'}>
                            <UserAvatar
                                user={{...expert, profilePicture: expert.profilePicture || headshotUrl}}
                                userId={expert.uid}
                                sx={{width: 96, height: 96, fontSize: '2rem'}}
                            />
                            <Stack spacing={0.75} flex={1}>
                                <Typography variant='h5' sx={{fontWeight: 700}}>{expert.displayName}</Typography>
                                {(expert.title || expert.office) && (
                                    <Typography sx={{color: 'text.secondary'}}>
                                        {asDisplayValue(expert.title)}{expert.office ? ` • ${asDisplayValue(expert.office)}` : ''}
                                    </Typography>
                                )}
                                <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                                    <Typography sx={{color: 'text.secondary'}}>{expert.organization}</Typography>
                                    <Typography sx={{color: 'text.secondary'}}>•</Typography>
                                    <Box component="img" src={'/assets/jabber.png'} alt="Jabber"
                                         sx={{width: 16, height: 16}}/>
                                    <Typography sx={{color: 'text.secondary'}}>{expert.handle}</Typography>
                                    {expert.expertiseUpdatedAt && (
                                        <>
                                            <Typography sx={{color: 'text.secondary'}}>•</Typography>
                                            <Tooltip title="Date the Expertise Summary was last auto-generated from this expert's published FINTELs" placement='top'>
                                                <Typography variant='body2' sx={{color: 'text.secondary'}}>
                                                    Profile last rebuild: {formatShortDate(expert.expertiseUpdatedAt)} ({updatedAgo})
                                                </Typography>
                                            </Tooltip>
                                        </>
                                    )}
                                </Stack>

                                {expert.uid && user?.uid && user.uid !== expert.uid &&
                                    <Stack direction='row' gap={1} pt={1} flexWrap='wrap'>
                                        <SendMessageButton isIcon={false} user={chatUser}/>
                                    </Stack>
                                }

                                {expert.expertise && (
                                    <Stack spacing={0.5} pt={1}>
                                        <Typography variant='body2' sx={{color: 'text.secondary', lineHeight: 1.2}}>
                                            Expertise Summary
                                        </Typography>
                                        <Typography variant='body1'>
                                            {expert.expertise}
                                        </Typography>
                                        <Typography variant='body2' sx={{color: 'text.secondary', fontStyle: 'italic'}}>
                                            Auto-generated from published FINTELs
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>
                        </Stack>

                        <Divider/>

                        <Stack spacing={3}>
                            <Stack spacing={3} direction={'row'}>
                                <Section title='Organization'>
                                    <Stack spacing={2}>
                                        <Stack {...twoCol}>
                                            <DetailRow label='Organization'>
                                                {asDisplayValue(expert.organization)}
                                            </DetailRow>
                                            <DetailRow label='Office'>
                                                {asDisplayValue(expert.office)}
                                            </DetailRow>
                                        </Stack>

                                        <Stack {...twoCol}>
                                            <DetailRow label='Location'>
                                                {asDisplayValue(expert.location)}
                                            </DetailRow>
                                            <DetailRow label='Clearance'>
                                                {asDisplayValue(expert.clearance)}
                                            </DetailRow>
                                        </Stack>

                                        <Stack spacing={0.5}>
                                            <Typography variant='overline'
                                                        sx={{color: 'text.secondary', lineHeight: 1.2}}>
                                                Estimated Relevance
                                            </Typography>
                                            <Tooltip title={relevanceTooltip} placement='top'>
                                                <Chip
                                                    label={expert.relevance}
                                                    sx={{textTransform: 'capitalize', width: 'fit-content'}}
                                                    icon={<Box sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        bgcolor: (theme) => theme.palette[paletteKey]?.main
                                                    }}/>}
                                                />
                                            </Tooltip>
                                        </Stack>
                                    </Stack>
                                </Section>

                                <Section title='Contact'>
                                    <Stack spacing={2}>
                                        <DetailRow label='Email'>
                                            {asDisplayValue(expert.email)}
                                        </DetailRow>

                                        <Stack {...twoCol}>
                                            <DetailRow label='Cell phone'>
                                                {formatPhone(expert.cellPhone)}
                                            </DetailRow>
                                            <DetailRow label='Landline'>
                                                {formatPhone(expert.landline)}
                                            </DetailRow>
                                        </Stack>

                                        <DetailRow label='TS-VoIP'>
                                            {formatTsVoip(expert.tsVoip)}
                                        </DetailRow>
                                    </Stack>
                                </Section>
                            </Stack>


                            <Section title='Expertise'>
                                <DetailRow label='Area of Responsibility (AoR)'>
                                    {expert.arr?.length ? expert.arr.join(', ') : '—'}
                                </DetailRow>

                                <Stack spacing={0.75}>
                                    <Typography variant='overline' sx={{color: 'text.secondary', lineHeight: 1.2}}>
                                        Specialties
                                    </Typography>
                                    <ListAsChips items={expert.specialties}/>
                                </Stack>

                                <Stack spacing={0.75}>
                                    <Typography variant='overline' sx={{color: 'text.secondary', lineHeight: 1.2}}>
                                        Languages
                                    </Typography>
                                    <ListAsChips items={expert.languages}/>
                                </Stack>
                            </Section>
                        </Stack>

                        <Divider/>

                        <Stack spacing={1}>
                            <Typography variant='h6'>Recent Contributions</Typography>
                            {recentFintels === null && (
                                <Typography variant='body2' color='text.secondary'>Loading…</Typography>
                            )}
                            {recentFintels !== null && recentFintels.length === 0 && (
                                <Typography variant='body2' color='text.secondary'>No accessible FINTELs found.</Typography>
                            )}
                            {recentFintels !== null && recentFintels.length > 0 && (
                                <Box component='ul' sx={{m: 0, pl: 2.5, listStyle: 'disc'}}>
                                    {recentFintels.map(r => {
                                        const productLabel = r.productType?.label || r.productType?.value || null;
                                        return (
                                            <Box component='li' key={r.id} sx={{mb: 0.75}}>
                                                <Stack direction='row' alignItems='baseline' spacing={1} flexWrap='wrap'>
                                                    <Link
                                                        component={RouterLink}
                                                        to={`/products/${r.id}`}
                                                        variant='body2'
                                                        sx={{
                                                            textDecoration: 'none',
                                                            color: 'primary.main',
                                                            '&:hover': {textDecoration: 'underline'},
                                                        }}
                                                    >
                                                        {r.title || r.meta?.title || 'Untitled'}
                                                    </Link>
                                                    {productLabel && (
                                                        <Chip
                                                            label={productLabel}
                                                            size='small'
                                                            variant='outlined'
                                                            sx={{height: 18, fontSize: '0.65rem', borderRadius: 1}}
                                                        />
                                                    )}
                                                    {r.updatedAt && (
                                                        <Typography variant='caption' color='text.secondary' noWrap>
                                                            {formatShortDate(r.updatedAt)}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Stack>
                    </Stack>
                </Paper>
            </Stack>
        </Stack>
    );
}
